<template>
  <info-text
    v-if="status"
    :type="status.type"
    :link="status.link ?? null"
    :button-label="status.linkLabel ?? null"
  >
    {{ status.text }}
    <div v-if="status.whatToDo" v-html="status.whatToDo" />

    <refresh-button
      v-if="canRetry"
      inverted
      class="mt-2 justify-start"
      :label="status.retry.label"
      :success-message="tr('Ad account updated successfully.')"
      :promise="retry"
    />
  </info-text>
</template>

<script>
import { ApiError } from '@/errors';
import AssetStatusMixin from '@/mixins/AssetStatus.js';
import ConnectFacebookProfileMixin from '@/mixins/ConnectFacebookProfile';
import InfoText from '@/ui/components/InfoText.vue';
import RefreshButton from '@/ui/components/RefreshButton.vue';

export default {
  name: 'AssetStatusMessage',
  components: {
    InfoText,
    RefreshButton,
  },
  mixins: [ AssetStatusMixin, ConnectFacebookProfileMixin ],
  props: {
    brand: {
      type: Object,
      required: false,
      default: () => null,
    },
    adAccount: {
      type: Object,
      required: false,
      default: () => null,
    },
    sourceProfile: {
      type: Object,
      required: false,
      default: () => null,
    },
    sourceProfiles: {
      type: Array,
      required: false,
      default: () => [],
    },
    showRetry: {
      type: Boolean,
      required: false,
      default: false,
    },
    isAiMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    status() {
      const sourceProfiles = [ ...this.sourceProfiles, this.sourceProfile ];

      const candidates = [
        () => this.brandStatusText(this.brand),
        () => this.adAccountStatusText(this.adAccount, this.isAiMode),
        ...sourceProfiles.map(sourceProfile => () => this.sourceProfileStatusText(sourceProfile)),
      ];

      for (const resolve of candidates) {
        const status = resolve();
        if (status) {
          return status;
        }
      }

      return null;
    },
    canRetry() {
      return this.showRetry && !!this.adAccount && !!this.status?.retry;
    },
  },
  methods: {
    retry() {
      const retryConfig = this.status?.retry;
      if (!retryConfig) {
        return Promise.reject(new Error('No retry configuration.'));
      }

      const targetExternalId = this.adAccount.external_id || this.adAccount.id;

      return this.connect(null, null, true)
        .then(resp => {
          let updatedAdAccount = null;

          const connectedProfiles = this.$root.loggedInUser.connected_profiles.filter(p => p.platform === 'facebook');
          for (const profile of connectedProfiles) {
            for (const adAcc of (profile.details?.ad_accounts || [])) {
              if (adAcc.id === targetExternalId) {
                updatedAdAccount = adAcc;
                break;
              }
            }
            if (updatedAdAccount !== null) {
              break;
            }
          }

          if (updatedAdAccount === null) {
            throw new ApiError(this.tr('After updating, the ad account cannot be found.'));
          }

          if (retryConfig.isStillFailing(updatedAdAccount)) {
            throw new ApiError(retryConfig.errorMessage);
          }

          this.$emit('retried', updatedAdAccount);

          return resp;
        });
    },
  },
};
</script>
